"use client";

import React, { useState, useEffect, useRef } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: 1,
  title: "BROWSE CRAVINGS",
  description: "Explore a real-time feed of local restaurant deals and nearby users in Toronto looking to split a meal."
}, {
  id: 2,
  title: "INSTANT MATCH",
  description: "Connect with a verified partner in your neighborhood who shares your taste and is ready to order right now."
}, {
  id: 3,
  title: "SPLIT THE BILL",
  description: "Forget the awkward math. Our secure payment system splits the cost instantly and automatically within the app."
}, {
  id: 4,
  title: "PICKUP & ENJOY",
  description: "Meet at the restaurant, grab your food, and enjoy premium Toronto dining at exactly half the price."
}];
export default function TheCrewProcess() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  const {
    ref: revealRef,
    isVisible
  } = useRevealOnScroll();
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const startOffset = windowHeight * 0.8;
      const endOffset = windowHeight * 0.2;
      const elementHeight = rect.height;
      const scrolled = startOffset - rect.top;
      const totalDistance = elementHeight + (startOffset - endOffset);
      let p = scrolled / totalDistance;
      p = Math.max(0, Math.min(1, p));
      setProgress(p * 100);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="50281" id="the-halforder-workflow" ref={node => {
    sectionRef.current = node;
    revealRef.current = node;
  }} className="dark bg-background py-24 relative overflow-hidden">
      <div className={cn("container mx-auto px-4 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
        {}
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-sans font-black tracking-tighter uppercase text-foreground mb-6">
            The HalfOrder Workflow
          </h2>
          <p className="text-lg text-muted-foreground font-sans leading-relaxed">
            Experience the future of social dining. Our streamlined system connects you 
            with nearby cravings in seconds, making premium food affordable for everyone.
          </p>
        </div>

        {}
        <div className="hidden md:block relative max-w-6xl mx-auto">
          {}
          <div className="absolute top-12 left-0 w-full h-1 bg-muted rounded-full -z-10" />
          
          {}
          <div className="absolute top-12 left-0 h-1 bg-primary rounded-full -z-10 transition-all duration-100 ease-linear" style={{
          width: `${progress}%`
        }} />

          <div className="grid grid-cols-4 gap-8">
            {steps.map((step, i) => {
            const stepPosition = i / (steps.length - 1) * 100;
            const isActive = progress >= stepPosition - 5;
            return <div key={step.id} className="flex flex-col items-center text-center group" data-index={i}>
                  {}
                  <div className={cn("w-24 h-24 rounded-full flex items-center justify-center text-3xl font-sans font-black mb-8 transition-all duration-500 border-4 z-10", isActive ? "bg-primary border-primary text-primary-foreground scale-110 shadow-[0_0_25px_rgba(255,102,0,0.4)]" : "bg-background border-muted text-muted-foreground scale-100")}>
                    0{step.id}
                  </div>
                  
                  {}
                  <div className={cn("transition-all duration-700 px-2", isActive ? "opacity-100 translate-y-0" : "opacity-40 translate-y-4")}>
                    <h3 className="text-xl font-sans font-black tracking-tight text-foreground mb-3 uppercase">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground font-sans leading-relaxed text-sm lg:text-base">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="md:hidden relative max-w-md mx-auto pl-4">
          {}
          <div className="absolute top-6 bottom-12 left-[2.5rem] w-1 bg-muted rounded-full -z-10 transform -translate-x-1/2" />
          
          {}
          <div className="absolute top-6 left-[2.5rem] w-1 bg-primary rounded-full -z-10 transform -translate-x-1/2 transition-all duration-100 ease-linear" style={{
          height: `${Math.min(progress * 1.2, 100)}%`,
          maxHeight: 'calc(100% - 3rem)'
        }} />

          <div className="flex flex-col gap-12">
            {steps.map((step, i) => {
            const stepPosition = i / (steps.length - 1) * 100;
            const isActive = progress >= stepPosition - 10;
            return <div key={step.id} className="flex gap-6 items-start" data-index={i}>
                  {}
                  <div className="flex-shrink-0 w-20 flex justify-center">
                    <div className={cn("w-16 h-16 rounded-full flex items-center justify-center text-xl font-sans font-black border-4 transition-all duration-500 z-10", isActive ? "bg-primary border-primary text-primary-foreground scale-110 shadow-[0_0_15px_rgba(255,102,0,0.3)]" : "bg-background border-muted text-muted-foreground scale-100")}>
                      0{step.id}
                    </div>
                  </div>
                  
                  {}
                  <div className={cn("pt-2 transition-all duration-500", isActive ? "opacity-100 translate-x-0" : "opacity-50 translate-x-4")}>
                    <h3 className="text-lg font-sans font-black tracking-tight text-foreground mb-2 uppercase">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground font-sans leading-relaxed text-sm">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}